/* eslint-disable */
/*!
 * dd-call-booker.js (v4 — multi-step wizard)
 *
 * Native Calendly scheduler client for /pages/welcome. 3-step wizard:
 *   1. CALENDAR  — month grid, pick a day
 *   2. TIME      — slot pills for that day, pick a time
 *   3. FORM      — name + email + phone, confirm booking
 *
 * Steps animate left/right with --dd-ease-smooth (reduced-motion swaps
 * instantly). Toasts via window.ddToast (cart-toast.js) for transient
 * feedback; inline banner fallback if ddToast is unavailable.
 *
 * ─── State machine ─────────────────────────────────────────────────
 *   INIT
 *     ├─ apiUrl missing                 → FALLBACK_EMBED (Liquid embed)
 *     └─ apiUrl present
 *           ↓
 *   LOADING_INITIAL  (month1 + month2 in parallel)
 *     ├─ success                        → CALENDAR
 *     ├─ 4xx (config)                   → FALLBACK_RUNTIME (re-attach embed)
 *     └─ retry once then fail           → ERROR_LOAD → FALLBACK_RUNTIME
 *
 *   CALENDAR  ← back from TIME
 *     ├─ pick day, cache fresh          → TIME
 *     ├─ pick day, cache stale          → LOADING_DAY → TIME
 *     └─ prev/next month (debounced 200ms)
 *           ↓
 *   TIME      ← back from FORM
 *     ├─ pick slot, start_time > now    → FORM
 *     └─ pick slot, start_time <= now   → toast + refetch day, stay
 *
 *   FORM
 *     └─ submit
 *           ↓
 *   SUBMITTING
 *     ├─ 200                            → CONFIRMED
 *     ├─ 409 (slot gone)                → toast + refetch day → TIME
 *     ├─ 429 (rate limit)               → toast + 10s submit cooldown, stay
 *     ├─ 4xx (validation)               → inline + toast, stay
 *     ├─ 5xx / network                  → toast, stay (ERROR_BOOK overlay)
 *
 * ─── Concurrency control ───────────────────────────────────────────
 *   Month flips debounced 200ms. Each fetchMonth/fetchDay carries a
 *   monotonic requestSeq + AbortController so superseded responses
 *   never overwrite newer state. Slot cache TTL 60s; day refreshed on
 *   TIME enter if stale; slot validated > now() on click (toast +
 *   refetch otherwise). Server-side 409 catches anything missed.
 *
 * ─── Fallback restoration ──────────────────────────────────────────
 *   On mount the original Liquid-rendered .dd-call-booker__glass
 *   (containing the Calendly inline embed + widget.js) is detached
 *   and stashed. FALLBACK_RUNTIME hides the scheduler container and
 *   re-attaches the stashed glass, re-loading Calendly's widget.js
 *   on the fly so the embed hydrates.
 */

/* ──────────────────────────────────────────────────────────────────
 * Swap controller + video player (v5 hero)
 *
 * The hero has a two-panel swap container (video ↔ scheduler). This IIFE:
 *   1. Wires the CTA button to swap from video → scheduler
 *   2. Wires the video play button to inject a Vimeo/YouTube iframe
 *      (URL is pre-computed by Liquid into data-video-embed)
 *
 * Runs independently of the scheduler IIFE below. If either fails the
 * other still works (graceful degradation).
 * ────────────────────────────────────────────────────────────────── */
(function () {
  'use strict';

  if (window._ddCallBookerSwapInit) return;
  window._ddCallBookerSwapInit = true;

  var swap = document.querySelector('[data-dd-swap]');
  if (!swap) return;

  var motionReduced = false;
  try {
    motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {}

  // ── 1. Video play button ──────────────────────────────────────────
  var videoMedia = swap.querySelector('[data-dd-video]');
  var playBtn = videoMedia && videoMedia.querySelector('.dd-call-booker__video-play');
  if (playBtn && videoMedia) {
    playBtn.addEventListener('click', function () {
      if (videoMedia.getAttribute('data-playing') === 'true') return;

      // Direct file (Shopify-hosted .mp4 etc.) → native <video>. Takes
      // precedence over the iframe embed path so a file URL plays inline
      // with native controls instead of trying to frame it.
      var fileUrl = videoMedia.getAttribute('data-video-file') || '';
      if (fileUrl) {
        var video = document.createElement('video');
        video.className = 'dd-call-booker__video-el';
        video.src = fileUrl;
        video.controls = true;
        video.autoplay = true;
        video.setAttribute('playsinline', '');
        video.setAttribute('preload', 'auto');
        // When the video finishes, automatically reveal the scheduler using
        // the same fade transition the CTA buttons trigger.
        video.addEventListener('ended', function () {
          if (typeof swapToScheduler === 'function') swapToScheduler();
        });
        videoMedia.appendChild(video);
        videoMedia.setAttribute('data-playing', 'true');
        var playPromise = video.play();
        if (playPromise && playPromise.catch) playPromise.catch(function () {});
        return;
      }

      var embedUrl = videoMedia.getAttribute('data-video-embed') || '';
      if (!embedUrl) return;
      var iframe = document.createElement('iframe');
      iframe.className = 'dd-call-booker__video-iframe';
      iframe.src = embedUrl;
      iframe.title = 'Founder call video';
      iframe.setAttribute('frameborder', '0');
      iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture; encrypted-media');
      iframe.setAttribute('allowfullscreen', '');
      videoMedia.appendChild(iframe);
      videoMedia.setAttribute('data-playing', 'true');
    });
  }

  // ── 2. CTA swap (video → scheduler) ───────────────────────────────
  var triggers = document.querySelectorAll('[data-dd-swap-trigger="scheduler"]');
  if (!triggers.length) return;

  /* Bring the hero swap into view before/while swapping. The hero CTA
     is already near the top so this is a near no-op for it; the Card A
     CTA lives below the fold, so without this the scheduler would
     reveal off-screen and look like nothing happened. Only scrolls
     when the swap is meaningfully out of the viewport. */
  function ensureVisible() {
    try {
      var r = swap.getBoundingClientRect();
      var vh = window.innerHeight || document.documentElement.clientHeight;
      if (r.top < 0 || r.top > vh * 0.5) {
        swap.scrollIntoView({ behavior: motionReduced ? 'auto' : 'smooth', block: 'start' });
      }
    } catch (_) {}
  }

  function swapToScheduler() {
    var videoPanel = swap.querySelector('[data-swap-panel="video"]');
    var schedulerPanel = swap.querySelector('[data-swap-panel="scheduler"]');
    if (!videoPanel || !schedulerPanel) return;
    ensureVisible();
    if (schedulerPanel.getAttribute('data-swap-state') === 'active') {
      // Already showing the scheduler — just make sure it's in view
      // (e.g. Card A clicked after a prior hero swap).
      return;
    }

    // Stop any playing video so audio doesn't bleed through during the fade
    var iframe = videoPanel.querySelector('.dd-call-booker__video-iframe');
    if (iframe && iframe.parentNode) iframe.parentNode.removeChild(iframe);
    var nativeVideo = videoPanel.querySelector('.dd-call-booker__video-el');
    if (nativeVideo) {
      try { nativeVideo.pause(); } catch (_) {}
      if (nativeVideo.parentNode) nativeVideo.parentNode.removeChild(nativeVideo);
    }
    var videoMediaEl = videoPanel.querySelector('[data-dd-video]');
    if (videoMediaEl) videoMediaEl.removeAttribute('data-playing');

    var FADE_MS = motionReduced ? 0 : 320;
    var GRACE_MS = motionReduced ? 0 : 120;

    // Phase 1: fade out video
    videoPanel.setAttribute('data-swap-state', 'fading-out');

    setTimeout(function () {
      // Phase 2: swap which panel is "rendered" — video → hidden, scheduler → fading-in
      videoPanel.setAttribute('data-swap-state', 'hidden');
      videoPanel.setAttribute('inert', '');
      schedulerPanel.setAttribute('data-swap-state', 'fading-in');
      schedulerPanel.removeAttribute('inert');

      // Phase 3: after grace period, trigger fade-in to opaque
      setTimeout(function () {
        // Two rAFs to ensure the browser commits the fading-in starting style
        // before transitioning to active.
        requestAnimationFrame(function () {
          requestAnimationFrame(function () {
            schedulerPanel.setAttribute('data-swap-state', 'active');
            // Calendly embed fallback hydrates only when visible — init on swap.
            var schedRoot = schedulerPanel.querySelector('.dd-call-booker__scheduler');
            if (schedRoot && typeof window.ddCallBookerHydrateEmbed === 'function') {
              window.ddCallBookerHydrateEmbed(schedRoot);
            }
            // Move focus into the scheduler for keyboard users
            var focusTarget =
              schedulerPanel.querySelector('[data-autofocus]') ||
              schedulerPanel.querySelector('button:not([disabled]), [tabindex="0"], input:not([disabled])');
            if (focusTarget && typeof focusTarget.focus === 'function') {
              try { focusTarget.focus({ preventScroll: false }); } catch (_) { focusTarget.focus(); }
            }
          });
        });
      }, GRACE_MS);
    }, FADE_MS);
  }

  for (var i = 0; i < triggers.length; i++) {
    triggers[i].addEventListener('click', swapToScheduler);
  }

  // Expose for debugging / programmatic triggering (e.g. analytics handlers)
  window.ddCallBookerSwap = { toScheduler: swapToScheduler };
})();

(function () {
  'use strict';

  // ── singleton guard ───────────────────────────────────────────────
  if (window._ddCallBookerInit) return;
  window._ddCallBookerInit = true;

  // ── config + container discovery ──────────────────────────────────
  var container = document.querySelector('.dd-call-booker__scheduler');
  if (!container) return; // not the welcome page

  var cfg = (typeof window !== 'undefined' && window.DD_CALENDLY_CONFIG) || {};
  var apiUrl = typeof cfg.apiUrl === 'string' ? cfg.apiUrl.replace(/\/+$/, '') : '';

  if (!apiUrl) {
    container.setAttribute('data-state', 'fallback-no-api-url');
    return;
  }

  var motionReduced = false;
  try {
    motionReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  } catch (_) {}

  var TRANSITION_MS = 320;

  // Hydrate the Liquid Calendly embed (fallback path). Must run after the
  // scheduler swap-panel is visible — init while hidden renders a blank box.
  function hydrateCalendlyEmbed(schedulerEl) {
    if (!schedulerEl) return;
    var embedUrl = schedulerEl.getAttribute('data-calendly-url') || '';
    var inlineDiv = schedulerEl.querySelector('.calendly-inline-widget');
    if (!inlineDiv || !embedUrl) return;

    function hideSkeleton() {
      var skel = schedulerEl.querySelector('.dd-call-booker__skeleton');
      if (skel) skel.style.display = 'none';
    }

    function initWidget() {
      if (!window.Calendly || typeof window.Calendly.initInlineWidget !== 'function') return false;
      try {
        // Re-init after a hidden-panel init leaves an empty widget shell.
        inlineDiv.innerHTML = '';
        window.Calendly.initInlineWidget({ url: embedUrl, parentElement: inlineDiv });
        hideSkeleton();
      } catch (e) {
        console.error('[dd-call-booker] Calendly init failed', e);
        return false;
      }
      return true;
    }

    if (initWidget()) return;

    var existingScript = document.querySelector('script[data-dd-calendly-widget]');
    if (existingScript) {
      existingScript.addEventListener('load', function () { initWidget(); }, { once: true });
      return;
    }
    var s = document.createElement('script');
    s.src = 'https://assets.calendly.com/assets/external/widget.js';
    s.async = true;
    s.setAttribute('data-dd-calendly-widget', '1');
    s.onload = function () { initWidget(); };
    document.head.appendChild(s);
  }
  window.ddCallBookerHydrateEmbed = hydrateCalendlyEmbed;

  // ── DOM helpers ───────────────────────────────────────────────────
  var SVG_NS = 'http://www.w3.org/2000/svg';

  function svgEl(tag, attrs) {
    var node = document.createElementNS(SVG_NS, tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v == null) continue;
        if (k === 'class') node.setAttribute('class', v);
        else node.setAttribute(k, String(v));
      }
    }
    return node;
  }

  function el(tag, attrs, children) {
    var node = document.createElement(tag);
    if (attrs) {
      for (var k in attrs) {
        if (!Object.prototype.hasOwnProperty.call(attrs, k)) continue;
        var v = attrs[k];
        if (v == null) continue;
        if (k === 'class') node.className = v;
        else if (k === 'text') node.textContent = v;
        else if (k === 'html') node.innerHTML = v;
        else if (k.indexOf('on') === 0 && typeof v === 'function') {
          node.addEventListener(k.slice(2).toLowerCase(), v);
        } else node.setAttribute(k, String(v));
      }
    }
    if (children) {
      var list = Array.isArray(children) ? children : [children];
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (c == null) continue;
        node.appendChild(typeof c === 'string' ? document.createTextNode(c) : c);
      }
    }
    return node;
  }
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }
  function nextFrame() {
    return new Promise(function (r) {
      requestAnimationFrame(function () {
        requestAnimationFrame(r);
      });
    });
  }
  function waitMs(ms) {
    return new Promise(function (r) {
      setTimeout(r, ms);
    });
  }

  // ── UUID + timezone ───────────────────────────────────────────────
  function uuidV4() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    var bytes = new Uint8Array(16);
    (window.crypto || window.msCrypto).getRandomValues(bytes);
    bytes[6] = (bytes[6] & 0x0f) | 0x40;
    bytes[8] = (bytes[8] & 0x3f) | 0x80;
    var hex = [];
    for (var i = 0; i < bytes.length; i++) hex.push((bytes[i] + 0x100).toString(16).slice(1));
    return (
      hex.slice(0, 4).join('') + '-' +
      hex.slice(4, 6).join('') + '-' +
      hex.slice(6, 8).join('') + '-' +
      hex.slice(8, 10).join('') + '-' +
      hex.slice(10, 16).join('')
    );
  }
  function detectTimezone() {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
    } catch (_) {
      return 'UTC';
    }
  }

  // ── Date helpers (viewer-local unless suffixed _utc) ──────────────
  function pad2(n) {
    return String(n).padStart(2, '0');
  }
  function utcYMD(date) {
    return date.getUTCFullYear() + '-' + pad2(date.getUTCMonth() + 1) + '-' + pad2(date.getUTCDate());
  }
  function localYMD(date, tz) {
    // YYYY-MM-DD in viewer's timezone (en-CA locale gives ISO format)
    return new Intl.DateTimeFormat('en-CA', {
      year: 'numeric', month: '2-digit', day: '2-digit', timeZone: tz,
    }).format(date);
  }
  function ymdFromParts(year, monthIndex, day) {
    return year + '-' + pad2(monthIndex + 1) + '-' + pad2(day);
  }
  function isAbortError(err) {
    if (!err) return false;
    if (err.name === 'AbortError') return true;
    if (err.code === 20 || err.code === 'ABORT_ERR') return true;
    var msg = String(err.message || '');
    return msg.indexOf('aborted') !== -1 || msg.indexOf('AbortError') !== -1;
  }
  function formatTime(date, tz) {
    return new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: tz,
    }).format(date);
  }
  function formatLongDate(date) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
    }).format(date);
  }
  function formatLongDateTime(date, tz) {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric',
      hour: 'numeric', minute: '2-digit', timeZoneName: 'short', timeZone: tz,
    }).format(date);
  }
  function formatTimezoneShort(date, tz) {
    try {
      var parts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        timeZoneName: 'short',
      }).formatToParts(date);
      for (var i = 0; i < parts.length; i++) {
        if (parts[i].type === 'timeZoneName') return parts[i].value;
      }
    } catch (_) {}
    return tz.replace(/_/g, ' ');
  }
  function formatConfirmedDetailLine(startDate, endDate, tz) {
    if (!startDate || isNaN(startDate.getTime())) return '';
    var end = endDate && !isNaN(endDate.getTime()) ? endDate : startDate;
    var datePart = new Intl.DateTimeFormat('en-US', {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz,
    }).format(startDate);
    var timePart = new Intl.DateTimeFormat('en-US', {
      hour: 'numeric', minute: '2-digit', timeZone: tz,
    }).format(startDate);
    var tzLabel = tz;
    var mins = Math.max(1, Math.round((end - startDate) / 60000));
    return datePart + ' \u2022 ' + timePart + ' \u2022 ' + tzLabel + ' \u2022 ' + mins + ' min';
  }
  function svgIcon(className, paths) {
    var svg = svgEl('svg', {
      class: className,
      viewBox: '0 0 24 24',
      fill: 'none',
      stroke: 'currentColor',
      'stroke-width': '1.6',
      'stroke-linecap': 'round',
      'stroke-linejoin': 'round',
      'aria-hidden': 'true',
    });
    for (var i = 0; i < paths.length; i++) {
      svg.appendChild(svgEl('path', { d: paths[i] }));
    }
    return svg;
  }
  function encodeCalText(value) {
    return encodeURIComponent(value || '').replace(/%20/g, '+');
  }
  function toIcsUtc(iso) {
    return new Date(iso).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}Z/, 'Z');
  }
  function buildCalendarEvent(booking, cfg) {
    var title = (cfg && cfg.eventTitle) || 'Founder call with Eric';
    var descriptionParts = ['Check your email confirmation for the link to join your meeting.'];
    if (booking.reschedule_url) descriptionParts.push('Reschedule: ' + booking.reschedule_url);
    return {
      title: title,
      start_time: booking.start_time,
      end_time: booking.end_time || booking.start_time,
      description: descriptionParts.join('\n'),
      location: 'Video call',
    };
  }
  function buildGoogleCalendarUrl(event) {
    var start = toIcsUtc(event.start_time);
    var end = toIcsUtc(event.end_time);
    return 'https://calendar.google.com/calendar/render?action=TEMPLATE'
      + '&text=' + encodeCalText(event.title)
      + '&dates=' + start + '/' + end
      + '&details=' + encodeCalText(event.description)
      + '&location=' + encodeCalText(event.location);
  }
  function buildOutlookCalendarUrl(event) {
    return 'https://outlook.live.com/calendar/0/deeplink/compose?'
      + 'subject=' + encodeCalText(event.title)
      + '&startdt=' + encodeURIComponent(event.start_time)
      + '&enddt=' + encodeURIComponent(event.end_time)
      + '&body=' + encodeCalText(event.description)
      + '&location=' + encodeCalText(event.location);
  }
  function downloadIcsFile(event) {
    var uid = (event.uid || Date.now()) + '@dropdeadextensions.com';
    var lines = [
      'BEGIN:VCALENDAR',
      'VERSION:2.0',
      'PRODID:-//Drop Dead//Welcome Booker//EN',
      'CALSCALE:GREGORIAN',
      'BEGIN:VEVENT',
      'UID:' + uid,
      'DTSTAMP:' + toIcsUtc(new Date().toISOString()),
      'DTSTART:' + toIcsUtc(event.start_time),
      'DTEND:' + toIcsUtc(event.end_time),
      'SUMMARY:' + (event.title || '').replace(/[,;\\]/g, '\\$&'),
      'DESCRIPTION:' + (event.description || '').replace(/\n/g, '\\n').replace(/[,;\\]/g, '\\$&'),
      'LOCATION:' + (event.location || '').replace(/[,;\\]/g, '\\$&'),
      'END:VEVENT',
      'END:VCALENDAR',
    ];
    var blob = new Blob([lines.join('\r\n')], { type: 'text/calendar;charset=utf-8' });
    var url = URL.createObjectURL(blob);
    var link = document.createElement('a');
    link.href = url;
    link.download = 'founder-call.ics';
    link.style.display = 'none';
    document.body.appendChild(link);
    link.click();
    setTimeout(function () {
      URL.revokeObjectURL(url);
      if (link.parentNode) link.parentNode.removeChild(link);
    }, 0);
  }
  function formatMonthYear(year, month) {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' })
      .format(new Date(year, month, 15));
  }
  function formatMonthDay(date) {
    return new Intl.DateTimeFormat('en-US', { month: 'long', day: 'numeric' }).format(date);
  }
  function shortTimezoneLabel(tz) {
    // Best-effort short label: en-US `timeZoneName: short` gives "EST" / "PST"
    try {
      var s = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'short' }).format(new Date());
      var m = s.match(/[A-Z]{2,5}$/);
      return m ? m[0] : tz.replace(/_/g, ' ');
    } catch (_) {
      return tz.replace(/_/g, ' ');
    }
  }
  function dateFromYMD(ymd) {
    var p = ymd.split('-');
    return new Date(parseInt(p[0], 10), parseInt(p[1], 10) - 1, parseInt(p[2], 10));
  }

  // E.164 for Calendly text_reminder_number (US-first, accepts + prefix).
  function isConfirmPreviewRequested() {
    try {
      if (/(?:^|[?&])dd_confirm_preview=1(?:&|$)/.test(window.location.search || '')) return true;
      if (window.location.hash === '#dd-confirm-preview') return true;
    } catch (_) {}
    return false;
  }
  function confirmPreviewExitUrl() {
    try {
      var url = new URL(window.location.href);
      url.searchParams.delete('dd_confirm_preview');
      url.hash = '';
      return url.pathname + url.search + url.hash;
    } catch (_) {
      return window.location.pathname;
    }
  }
  function buildConfirmPreviewBooking(booker) {
    var start = new Date();
    start.setDate(start.getDate() + 7);
    start.setHours(13, 0, 0, 0);
    var end = new Date(start.getTime() + 30 * 60000);
    return {
      uri: 'https://api.calendly.com/scheduled_events/dd-preview-mock',
      start_time: start.toISOString(),
      end_time: end.toISOString(),
      name: booker.customerName || 'Alex Day',
      email: booker.customerEmail || 'you@example.com',
      phone: booker.customerPhone || '+15555551234',
      join_url: null,
      reschedule_url: 'https://calendly.com/reschedulings/dd-preview-mock',
      cancel_url: 'https://calendly.com/cancellations/dd-preview-mock',
    };
  }

  function normalizePhoneToE164(raw) {
    if (!raw || typeof raw !== 'string') return '';
    var s = raw.trim();
    if (!s) return '';
    if (s.indexOf('+') === 0) {
      var intlDigits = s.slice(1).replace(/\D/g, '');
      if (intlDigits.length >= 10 && intlDigits.length <= 15) return '+' + intlDigits;
      return '';
    }
    var digits = s.replace(/\D/g, '');
    if (digits.length === 10) return '+1' + digits;
    if (digits.length === 11 && digits.charAt(0) === '1') return '+' + digits;
    if (digits.length >= 10 && digits.length <= 15) return '+' + digits;
    return '';
  }

  // ── component ─────────────────────────────────────────────────────
  function Booker(root) {
    this.root = root;
    this.customerName = root.getAttribute('data-customer-name') || '';
    this.customerEmail = root.getAttribute('data-customer-email') || '';
    this.customerPhone = root.getAttribute('data-customer-phone') || '';
    this.fallbackLink = root.getAttribute('data-fallback-link') || '';
    this.fallbackEmbedUrl = root.getAttribute('data-calendly-url') || '';
    this.timezone = detectTimezone();
    this.confirmCfg = (typeof window !== 'undefined' && window.DD_CALL_BOOKER_CONFIRM) || {};

    // Cursor + selections
    var now = new Date();
    this.currentMonth = { year: now.getFullYear(), month: now.getMonth() };
    this.selectedDay = null;     // 'YYYY-MM-DD' viewer-local
    this.selectedSlot = null;

    // State
    this.state = 'INIT';
    this.lastError = null;
    this.confirmation = null;
    this.idemKey = null;
    this.submitCooldownUntil = 0;

    // Caches + concurrency
    this.slotsByMonth = {};      // 'YYYY-MM' -> { days: { 'YYYY-MM-DD': [...] }, loadedAt }
    this.requestSeqMonth = {};
    this.requestSeqDay = {};
    this.abortMonth = {};
    this.abortDay = {};
    this.monthFlipTimer = null;
    this.transitionLock = false;
    this.bannerTimer = null;

    // Fallback DOM stash
    this.fallbackGlass = null;

    // DOM refs (set in mount)
    this.shell = null;
    this.bannerHost = null;
    this.viewport = null;
  }

  // ── mount ────────────────────────────────────────────────────────
  Booker.prototype.mount = function () {
    // Detach the original Liquid-rendered glass (with Calendly embed) for
    // potential FALLBACK_RUNTIME re-attachment. We do this before clearing
    // the container so the embed's <script> tag (already in DOM) doesn't
    // get garbage-collected and we can re-attach it whole.
    var existingGlass = this.root.querySelector('.dd-call-booker__glass');
    if (existingGlass) {
      this.fallbackGlass = existingGlass;
      existingGlass.parentNode.removeChild(existingGlass);
    }

    clear(this.root);
    this.root.setAttribute('data-state', 'loading');

    this.shell = el('div', { class: 'dd-call-booker__glass dd-call-booker__glass--scheduler' });
    this.bannerHost = el('div', { class: 'dd-sched__banner-host', 'aria-live': 'polite', 'aria-atomic': 'true' });
    this.viewport = el('div', { class: 'dd-sched__viewport' });
    this.shell.appendChild(this.bannerHost);
    this.shell.appendChild(this.viewport);
    this.root.appendChild(this.shell);

    this.bindGlobalKeys();

    if (this.root.getAttribute('data-confirm-preview-allowed') === 'true' && isConfirmPreviewRequested()) {
      this.showConfirmPreview();
      return;
    }

    // Initial loading view (no transition — first paint)
    var loading = this.renderLoading();
    loading.classList.add('dd-sched__view');
    this.viewport.appendChild(loading);

    this.loadInitial();
  };

  // ── ESC = back ───────────────────────────────────────────────────
  Booker.prototype.bindGlobalKeys = function () {
    var self = this;
    self.root.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      if (self.transitionLock) return;
      if (self.state === 'TIME') {
        e.preventDefault();
        self.backToCalendar();
      } else if (self.state === 'FORM') {
        e.preventDefault();
        self.backToTime();
      }
    });
  };

  // ── initial load (current month + next month in parallel) ────────
  Booker.prototype.loadInitial = function () {
    var self = this;
    self.state = 'LOADING_INITIAL';
    self.root.setAttribute('data-state', 'loading');

    var nextCursor = self.shiftMonth(self.currentMonth, 1);

    Promise.allSettled([
      self.fetchMonth(self.currentMonth.year, self.currentMonth.month),
      self.fetchMonth(nextCursor.year, nextCursor.month),
    ]).then(function (results) {
      var m1 = results[0];
      if (m1.status === 'rejected') {
        // Retry once (includes transient Calendly 400s). Do not jump straight
        // to the hidden-panel embed fallback — it renders blank until swap.
        self.fetchMonth(self.currentMonth.year, self.currentMonth.month).then(function (data) {
          if (data && data.aborted) return;
          self.skipToFirstAvailableMonth(6).then(function () { self.enterCalendar(); });
        }).catch(function () {
          self.state = 'ERROR_LOAD';
          self.root.setAttribute('data-state', 'error-load');
          self.swapView(self.renderErrorLoad(), 'forward');
          self.emitToast('Calendly is having issues. Try again.', 'error', 6000);
          setTimeout(function () {
            if (self.state === 'ERROR_LOAD') self.activateFallback('slots-failed-twice');
          }, 6000);
        });
        return;
      }
      if (m1.status === 'fulfilled' && m1.value && m1.value.aborted) return;
      self.skipToFirstAvailableMonth(6).then(function () { self.enterCalendar(); });
    });
  };

  Booker.prototype.enterCalendar = function () {
    this.state = 'CALENDAR';
    this.root.setAttribute('data-state', 'calendar');
    this.swapView(this.renderCalendar(), 'forward');
  };

  // ── auto-advance to the first month that has availability ────────
  // True when the cached month has at least one day with a bookable slot.
  Booker.prototype.monthHasAvailability = function (year, month) {
    var data = this.slotsByMonth[this.monthKey(year, month)];
    if (!data || !data.days) return false;
    var keys = Object.keys(data.days);
    for (var i = 0; i < keys.length; i++) {
      if (data.days[keys[i]] && data.days[keys[i]].length > 0) return true;
    }
    return false;
  };

  // If the current month view has no bookable slots, walk forward
  // (fetching each month) until one with availability is found, so the
  // calendar opens on a useful month instead of an empty grid. Capped at
  // the same 6-month soft limit the next-arrow uses. The current + next
  // months are already cached by loadInitial, so the common case adds no
  // extra requests. If every month in range is empty, currentMonth is left
  // unchanged and the empty-month banner shows as before.
  Booker.prototype.skipToFirstAvailableMonth = function (maxMonths) {
    var self = this;
    maxMonths = maxMonths || 6;
    if (self.monthHasAvailability(self.currentMonth.year, self.currentMonth.month)) {
      return Promise.resolve();
    }
    var attempts = 0;
    function step(cursor) {
      if (attempts >= maxMonths) return Promise.resolve();
      attempts++;
      var next = self.shiftMonth(cursor, 1);
      return self.fetchMonth(next.year, next.month).then(function () {
        if (self.monthHasAvailability(next.year, next.month)) {
          self.currentMonth = next;
          return;
        }
        return step(next);
      }).catch(function () {
        // Stop scanning on fetch error; leave currentMonth as-is.
        return;
      });
    }
    return step(self.currentMonth);
  };

  // ── FALLBACK_RUNTIME: re-attach detached embed ───────────────────
  Booker.prototype.activateFallback = function (reason) {
    console.warn('[dd-call-booker] FALLBACK_RUNTIME:', reason);
    this.state = 'FALLBACK_RUNTIME';
    this.root.setAttribute('data-state', 'fallback-runtime');

    if (!this.fallbackGlass) {
      // No embed to restore — leave scheduler showing ERROR_LOAD with retry
      return;
    }

    // Tear down the custom scheduler shell and put the embed glass back
    // in its original parent (this.root === .dd-call-booker__scheduler).
    clear(this.root);
    this.root.appendChild(this.fallbackGlass);
    // Embed init runs in swapToScheduler when the panel is visible — init while
    // hidden (inert + opacity 0) renders a blank iframe shell.
  };

  // ── Month / day arithmetic ───────────────────────────────────────
  Booker.prototype.monthKey = function (year, month) {
    return year + '-' + pad2(month + 1);
  };
  Booker.prototype.shiftMonth = function (cursor, delta) {
    var d = new Date(cursor.year, cursor.month + delta, 1);
    return { year: d.getFullYear(), month: d.getMonth() };
  };
  Booker.prototype.daysInMonth = function (year, month) {
    return new Date(year, month + 1, 0).getDate();
  };
  Booker.prototype.firstWeekday = function (year, month) {
    return new Date(year, month, 1).getDay();
  };
  // Build ≤7-day fetch windows using calendar day numbers (no time-of-day /
  // utcYMD skew). Prevents inverted start/end pairs that return HTTP 400.
  Booker.prototype.buildMonthFetchWindows = function (year, month) {
    var daysInMonth = this.daysInMonth(year, month);
    var now = new Date();
    var todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var monthEndDate = new Date(year, month, daysInMonth);

    if (monthEndDate < todayStart) return [];

    var startDay = 1;
    if (year === now.getFullYear() && month === now.getMonth()) {
      startDay = now.getDate();
    }

    var windows = [];
    var d = startDay;
    while (d <= daysInMonth) {
      var winEnd = Math.min(d + 6, daysInMonth);
      windows.push({
        start: ymdFromParts(year, month, d),
        end: ymdFromParts(year, month, winEnd),
      });
      d = winEnd + 1;
    }
    return windows;
  };

  // ── fetch a month's slots (request-token + abort-safe) ──────────
  Booker.prototype.fetchMonth = function (year, month) {
    var self = this;
    var key = self.monthKey(year, month);

    // Fresh cache hit → resolve immediately
    var cached = self.slotsByMonth[key];
    if (cached && Date.now() - cached.loadedAt < 60000) {
      return Promise.resolve(cached);
    }

    var seq = (self.requestSeqMonth[key] || 0) + 1;
    self.requestSeqMonth[key] = seq;
    if (self.abortMonth[key]) {
      try { self.abortMonth[key].abort(); } catch (_) {}
    }
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    self.abortMonth[key] = ac;

    var daysInMonth = self.daysInMonth(year, month);
    var now = new Date();
    var todayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    var monthEndDate = new Date(year, month, daysInMonth);

    // Skip months entirely in the past
    if (monthEndDate < todayStart) {
      var empty = { days: {}, loadedAt: Date.now() };
      self.slotsByMonth[key] = empty;
      return Promise.resolve(empty);
    }

    var windows = self.buildMonthFetchWindows(year, month);
    if (!windows.length) {
      var emptyNow = { days: {}, loadedAt: Date.now() };
      self.slotsByMonth[key] = emptyNow;
      return Promise.resolve(emptyNow);
    }

    var init = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (ac) init.signal = ac.signal;

    function fetchWindow(win, attempt) {
      var url = apiUrl + '/api/calendly/slots?start_date=' + win.start + '&end_date=' + win.end;
      return fetch(url, init).then(function (res) {
        if ((res.status === 429 || res.status === 400 || res.status === 502) && attempt < 1) {
          var retryAfter = res.status === 429
            ? parseInt(res.headers.get('Retry-After') || '2', 10)
            : 1;
          var delayMs = Math.min(Math.max(retryAfter, 1), 10) * 1000;
          return waitMs(delayMs).then(function () { return fetchWindow(win, attempt + 1); });
        }
        if (!res.ok) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            var err = new Error((body.error && body.error.message) || 'slots failed');
            err.status = res.status;
            err.code = body.error && body.error.code;
            throw err;
          });
        }
        return res.json().then(function (body) { return (body && body.slots) || []; });
      });
    }

    var fetches = windows.map(function (win) { return fetchWindow(win, 0); });

    return Promise.allSettled(fetches).then(function (results) {
      if (seq !== self.requestSeqMonth[key]) {
        return { aborted: true };
      }
      var all = [];
      var failed = 0;
      results.forEach(function (r, idx) {
        if (r.status === 'fulfilled') {
          all = all.concat(r.value);
        } else {
          failed++;
          console.warn('[dd-call-booker] slots window failed', windows[idx], r.reason);
        }
      });
      if (!all.length && failed === results.length) {
        var err = results[0].reason || new Error('slots failed');
        throw err;
      }
      var byDay = {};
      all.forEach(function (s) {
        var d = new Date(s.start_time);
        var dayKey = localYMD(d, self.timezone);
        if (!byDay[dayKey]) byDay[dayKey] = [];
        byDay[dayKey].push(s);
      });
      Object.keys(byDay).forEach(function (k) {
        byDay[k].sort(function (a, b) { return new Date(a.start_time) - new Date(b.start_time); });
      });
      var data = { days: byDay, loadedAt: Date.now() };
      self.slotsByMonth[key] = data;
      return data;
    }).catch(function (err) {
      if (isAbortError(err)) return { aborted: true };
      throw err;
    });
  };

  // ── fetch a single day (re-uses month-window endpoint) ──────────
  Booker.prototype.fetchDay = function (yyyymmdd) {
    var self = this;
    var seq = (self.requestSeqDay[yyyymmdd] || 0) + 1;
    self.requestSeqDay[yyyymmdd] = seq;
    if (self.abortDay[yyyymmdd]) {
      try { self.abortDay[yyyymmdd].abort(); } catch (_) {}
    }
    var ac = typeof AbortController === 'function' ? new AbortController() : null;
    self.abortDay[yyyymmdd] = ac;

    var init = { method: 'GET', credentials: 'omit', headers: { Accept: 'application/json' } };
    if (ac) init.signal = ac.signal;

    // Use yyyymmdd as both start & end (UTC interpretation by proxy);
    // we filter to the viewer-local day client-side after fetch.
    var url = apiUrl + '/api/calendly/slots?start_date=' + yyyymmdd + '&end_date=' + yyyymmdd;
    return fetch(url, init).then(function (res) {
      if (!res.ok) {
        var e = new Error('day fetch failed');
        e.status = res.status;
        throw e;
      }
      return res.json();
    }).then(function (body) {
      if (seq !== self.requestSeqDay[yyyymmdd]) return null;
      var raw = (body && body.slots) || [];
      // Re-bucket to viewer-local YYYY-MM-DD
      var matched = raw.filter(function (s) {
        return localYMD(new Date(s.start_time), self.timezone) === yyyymmdd;
      });
      matched.sort(function (a, b) {
        return new Date(a.start_time) - new Date(b.start_time);
      });
      // Merge into month cache
      var parts = yyyymmdd.split('-');
      var mKey = parts[0] + '-' + parts[1];
      if (self.slotsByMonth[mKey]) {
        self.slotsByMonth[mKey].days[yyyymmdd] = matched;
        // Don't bump loadedAt — month cache is wider; just freshen this day
      }
      return matched;
    });
  };

  // ── view swap (forward = slide L→R off, R→L on) ──────────────────
  Booker.prototype.swapView = function (newView, direction) {
    var self = this;
    if (self.transitionLock) {
      // Coalesce — last swap wins. Replace inert flag handling so we don't deadlock.
    }
    newView.classList.add('dd-sched__view');

    if (motionReduced) {
      clear(self.viewport);
      self.viewport.appendChild(newView);
      self.focusFirstInteractive(newView);
      return Promise.resolve();
    }

    var oldView = self.viewport.querySelector('.dd-sched__view');
    if (!oldView) {
      self.viewport.appendChild(newView);
      self.focusFirstInteractive(newView);
      return Promise.resolve();
    }

    self.transitionLock = true;
    self.viewport.setAttribute('inert', '');

    var enterClass = direction === 'back' ? 'dd-sched__view--enter-left' : 'dd-sched__view--enter-right';
    var exitClass = direction === 'back' ? 'dd-sched__view--exit-right' : 'dd-sched__view--exit-left';

    newView.classList.add(enterClass);
    self.viewport.appendChild(newView);

    return nextFrame().then(function () {
      newView.classList.remove(enterClass);
      oldView.classList.add(exitClass);
      return waitMs(TRANSITION_MS + 60);
    }).then(function () {
      if (oldView.parentNode === self.viewport) self.viewport.removeChild(oldView);
      self.viewport.removeAttribute('inert');
      self.transitionLock = false;
      self.focusFirstInteractive(newView);
    }).catch(function (err) {
      console.error('[dd-call-booker] swapView failed', err);
      newView.classList.remove('dd-sched__view--enter-left', 'dd-sched__view--enter-right');
      newView.style.opacity = '1';
      newView.style.transform = 'none';
      self.viewport.removeAttribute('inert');
      self.transitionLock = false;
    });
  };

  // ── replace current view in-place (no animation, same step) ─────
  Booker.prototype.replaceView = function (newView) {
    var self = this;
    newView.classList.add('dd-sched__view');
    var old = self.viewport.querySelector('.dd-sched__view');
    if (old) self.viewport.replaceChild(newView, old);
    else self.viewport.appendChild(newView);
    self.focusFirstInteractive(newView);
  };

  Booker.prototype.focusFirstInteractive = function (view) {
    if (!view) return;
    var target = view.querySelector('[data-autofocus]') ||
                 view.querySelector('button:not([disabled]), input:not([disabled]), [tabindex="0"]');
    if (target) {
      try { target.focus({ preventScroll: true }); } catch (_) {}
    }
  };

  // ── toast (ddToast preferred, inline banner fallback) ───────────
  Booker.prototype.emitToast = function (msg, type, duration) {
    type = type || 'info';
    duration = duration || 4500;
    if (typeof window.ddToast === 'function') {
      try {
        window.ddToast(msg, { type: type, duration: duration });
        return;
      } catch (_) {}
    }
    this.showInlineBanner(msg, type, duration);
  };

  Booker.prototype.showInlineBanner = function (msg, type, duration) {
    var self = this;
    if (self.bannerTimer) {
      clearTimeout(self.bannerTimer);
      self.bannerTimer = null;
    }
    clear(self.bannerHost);
    var banner = el('div', {
      class: 'dd-sched__banner dd-sched__banner--' + type,
      role: type === 'error' ? 'alert' : 'status',
    }, [
      el('span', { class: 'dd-sched__banner-msg', text: msg }),
      el('button', {
        class: 'dd-sched__banner-close',
        type: 'button',
        'aria-label': 'Dismiss',
        onclick: function () {
          clear(self.bannerHost);
          if (self.bannerTimer) { clearTimeout(self.bannerTimer); self.bannerTimer = null; }
        },
      }, '\u00d7'),
    ]);
    self.bannerHost.appendChild(banner);
    self.bannerTimer = setTimeout(function () {
      clear(self.bannerHost);
      self.bannerTimer = null;
    }, duration);
  };

  // ── LOADING (initial + retry) ────────────────────────────────────
  Booker.prototype.renderLoading = function () {
    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--loading',
      'aria-live': 'polite', 'aria-busy': 'true',
    }, [
      el('div', { class: 'dd-sched__spinner', 'aria-hidden': 'true' }),
      el('p', { class: 'dd-sched__loading-text', text: 'Loading available times\u2026' }),
    ]);
  };

  // ── CALENDAR ─────────────────────────────────────────────────────
  Booker.prototype.renderCalendar = function () {
    var self = this;
    var year = self.currentMonth.year;
    var month = self.currentMonth.month;
    var key = self.monthKey(year, month);
    var monthData = self.slotsByMonth[key];
    var availableDays = (monthData && monthData.days) || {};

    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--calendar' });

    view.appendChild(el('p', {
      class: 'dd-sched__step-indicator',
      text: 'Step 1 of 3 \u2014 Pick a day',
    }));

    // Header with prev/next nav
    var now = new Date();
    var thisMonthFirst = new Date(now.getFullYear(), now.getMonth(), 1);
    var displayedFirst = new Date(year, month, 1);
    var prevDisabled = displayedFirst <= thisMonthFirst;
    // No hard cap on next — backend covers what it covers; user can flip forward freely
    // but we soft-cap at 6 months ahead to prevent infinite scrolling.
    var sixMonthsOut = new Date(now.getFullYear(), now.getMonth() + 6, 1);
    var nextDisabled = displayedFirst >= sixMonthsOut;

    var header = el('div', { class: 'dd-sched__cal-header' }, [
      el('button', {
        class: 'dd-sched__cal-nav dd-sched__cal-nav--prev',
        type: 'button',
        'aria-label': 'Previous month',
        disabled: prevDisabled ? 'disabled' : null,
        onclick: prevDisabled ? null : function () { self.flipMonth(-1); },
      }, '\u2039'),
      el('h3', {
        class: 'dd-sched__cal-month',
        text: formatMonthYear(year, month),
        'aria-live': 'polite',
      }),
      el('button', {
        class: 'dd-sched__cal-nav dd-sched__cal-nav--next',
        type: 'button',
        'aria-label': 'Next month',
        'data-autofocus': '',
        disabled: nextDisabled ? 'disabled' : null,
        onclick: nextDisabled ? null : function () { self.flipMonth(1); },
      }, '\u203A'),
    ]);
    view.appendChild(header);

    // Day-of-week header
    var dowHeader = el('div', { class: 'dd-sched__cal-dow', role: 'row' });
    var dowShort = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
    var dowFull = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    for (var d = 0; d < 7; d++) {
      dowHeader.appendChild(el('div', {
        class: 'dd-sched__cal-dow-cell',
        role: 'columnheader',
        'aria-label': dowFull[d],
        text: dowShort[d],
      }));
    }
    view.appendChild(dowHeader);

    // Calendar grid — place each day with explicit grid-column/row.
    // Do NOT use empty leading/trailing filler divs: Dawn base.css sets
    // div:empty { display: none }, which collapses padding cells and pins
    // the 1st of every month under Sunday regardless of weekday.
    var grid = el('div', { class: 'dd-sched__cal-grid', role: 'grid', 'aria-label': formatMonthYear(year, month) });
    var firstW = self.firstWeekday(year, month);
    var daysCount = self.daysInMonth(year, month);
    var totalRows = Math.ceil((firstW + daysCount) / 7);
    grid.style.gridTemplateRows = 'repeat(' + totalRows + ', minmax(44px, auto))';
    var todayKey = localYMD(now, self.timezone);
    var availableCount = 0;

    for (var dayNum = 1; dayNum <= daysCount; dayNum++) {
      var cellIndex = firstW + dayNum - 1;
      var gridCol = (cellIndex % 7) + 1;
      var gridRow = Math.floor(cellIndex / 7) + 1;
      var cellDate = new Date(year, month, dayNum);
      var cellKey = localYMD(cellDate, self.timezone);
      var slotsForCell = availableDays[cellKey] || [];
      var hasAvail = slotsForCell.length > 0;
      if (hasAvail) availableCount++;
      var isToday = cellKey === todayKey;
      var isSelected = self.selectedDay === cellKey;
      var endOfCellDay = new Date(year, month, dayNum, 23, 59, 59);
      var isPast = endOfCellDay < now;
      var disabled = isPast || !hasAvail;

      var aria = formatMonthDay(cellDate);
      aria += hasAvail
        ? ', ' + slotsForCell.length + ' time' + (slotsForCell.length === 1 ? '' : 's') + ' available'
        : (isPast ? ', past' : ', no availability');

      var cellClass = 'dd-sched__cal-cell';
      if (isToday) cellClass += ' dd-sched__cal-cell--today';
      if (isSelected) cellClass += ' dd-sched__cal-cell--selected';
      if (isPast) cellClass += ' dd-sched__cal-cell--past';
      if (hasAvail && !isPast) cellClass += ' dd-sched__cal-cell--available';
      if (disabled) cellClass += ' dd-sched__cal-cell--disabled';

      var attrs = {
        class: cellClass,
        type: 'button',
        role: 'gridcell',
        style: 'grid-column:' + gridCol + ';grid-row:' + gridRow,
        'aria-label': aria,
        'aria-disabled': disabled ? 'true' : null,
        'aria-current': isToday ? 'date' : null,
        'aria-selected': isSelected ? 'true' : null,
        disabled: disabled ? 'disabled' : null,
      };
      if (!disabled) {
        (function (dayKey) {
          attrs.onclick = function () { self.pickDay(dayKey); };
        })(cellKey);
      }

      grid.appendChild(el('button', attrs, [
        el('span', { class: 'dd-sched__cal-cell-num', text: String(dayNum) }),
        hasAvail && !isPast ? el('span', { class: 'dd-sched__cal-cell-dot', 'aria-hidden': 'true' }) : null,
      ]));
    }
    view.appendChild(grid);

    // Empty-month banner
    if (availableCount === 0 && monthData) {
      view.appendChild(el('p', {
        class: 'dd-sched__cal-empty',
        text: 'No availability this month \u2014 try the next month \u203A',
      }));
    }

    view.appendChild(el('p', {
      class: 'dd-sched__cal-footer',
      text: '30 min with Eric \u00b7 Times in ' + shortTimezoneLabel(self.timezone),
    }));

    return view;
  };

  // ── month flip (debounced, request-token guarded) ───────────────
  Booker.prototype.flipMonth = function (delta) {
    var self = this;
    if (self.monthFlipTimer) clearTimeout(self.monthFlipTimer);
    // Optimistically shift cursor so rapid clicks accumulate
    self.currentMonth = self.shiftMonth(self.currentMonth, delta);
    self.monthFlipTimer = setTimeout(function () {
      self.commitMonthFlip(delta > 0 ? 'forward' : 'back');
    }, 180);
  };

  Booker.prototype.commitMonthFlip = function (direction) {
    var self = this;
    var year = self.currentMonth.year;
    var month = self.currentMonth.month;
    var key = self.monthKey(year, month);

    var cached = self.slotsByMonth[key];
    var fresh = cached && Date.now() - cached.loadedAt < 60000;

    // Always swap visually first (with the data we have)
    self.swapView(self.renderCalendar(), direction);

    if (!fresh) {
      self.fetchMonth(year, month).then(function (data) {
        if (!data || data.aborted) return;
        if (self.state !== 'CALENDAR') return;
        // Only re-render if we're still on this month
        if (self.monthKey(self.currentMonth.year, self.currentMonth.month) !== key) return;
        self.replaceView(self.renderCalendar());
      }).catch(function (err) {
        if (isAbortError(err)) return;
        if (self.monthKey(self.currentMonth.year, self.currentMonth.month) !== key) return;
        console.error('[dd-call-booker] month fetch failed', err);
        if (err && err.status === 429) {
          self.emitToast('Too many requests. Wait a moment, then try that month again.', 'warn', 5000);
          return;
        }
        self.emitToast("Couldn't load that month. Try another.", 'error');
      });
    }
  };

  // ── day pick → TIME ─────────────────────────────────────────────
  Booker.prototype.pickDay = function (dayKey) {
    var self = this;
    if (self.transitionLock) return;
    self.selectedDay = dayKey;
    var monthData = self.slotsByMonth[self.monthKey(self.currentMonth.year, self.currentMonth.month)];
    var stale = !monthData || Date.now() - monthData.loadedAt > 60000;

    if (stale) {
      // Show TIME view with loading state, refetch in background, then re-render
      self.state = 'TIME';
      self.root.setAttribute('data-state', 'time');
      self.swapView(self.renderTimeLoading(dayKey), 'forward');
      self.fetchDay(dayKey).then(function (slots) {
        if (slots === null) return; // superseded
        if (self.state === 'TIME' && self.selectedDay === dayKey) {
          self.replaceView(self.renderTime(dayKey));
        }
      }).catch(function (err) {
        if (isAbortError(err)) return;
        self.emitToast("Couldn't load times for that day. Try another.", 'error');
        self.backToCalendar();
      });
      return;
    }

    self.state = 'TIME';
    self.root.setAttribute('data-state', 'time');
    self.swapView(self.renderTime(dayKey), 'forward');
  };

  // ── TIME ─────────────────────────────────────────────────────────
  Booker.prototype.renderTime = function (dayKey) {
    var self = this;
    var monthData = self.slotsByMonth[self.monthKey(self.currentMonth.year, self.currentMonth.month)];
    var slots = (monthData && monthData.days[dayKey]) || [];
    // Filter out any that have slipped into the past mid-browse
    var now = Date.now();
    slots = slots.filter(function (s) { return new Date(s.start_time).getTime() > now; });
    var dayDate = dateFromYMD(dayKey);

    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--time' });

    view.appendChild(self.renderStepHeader('calendar', 'Step 2 of 3 \u2014 Pick a time'));

    view.appendChild(el('h3', {
      class: 'dd-sched__time-day',
      text: formatLongDate(dayDate),
    }));

    if (slots.length === 0) {
      view.appendChild(el('p', {
        class: 'dd-sched__time-empty',
        text: 'No times left for this day. Pick another day.',
      }));
      return view;
    }

    var grid = el('div', { class: 'dd-sched__time-grid', role: 'list' });
    slots.forEach(function (slot) {
      var d = new Date(slot.start_time);
      var label = formatTime(d, self.timezone);
      var aria = label + ' on ' + formatLongDate(dayDate);
      grid.appendChild(el('button', {
        class: 'dd-sched__time-slot',
        type: 'button',
        role: 'listitem',
        'data-start': slot.start_time,
        'aria-label': aria,
        onclick: function () { self.pickSlot(slot); },
      }, label));
    });
    view.appendChild(grid);

    view.appendChild(el('p', {
      class: 'dd-sched__time-tz',
      text: 'All times in ' + shortTimezoneLabel(self.timezone),
    }));

    return view;
  };

  Booker.prototype.renderTimeLoading = function (dayKey) {
    var self = this;
    var dayDate = dateFromYMD(dayKey);
    var view = el('div', { class: 'dd-sched__view-inner dd-sched__view-inner--time' });
    view.appendChild(self.renderStepHeader('calendar', 'Step 2 of 3 \u2014 Pick a time'));
    view.appendChild(el('h3', {
      class: 'dd-sched__time-day',
      text: formatLongDate(dayDate),
    }));
    view.appendChild(el('div', { class: 'dd-sched__spinner', 'aria-hidden': 'true' }));
    view.appendChild(el('p', { class: 'dd-sched__time-loading', text: 'Loading times\u2026' }));
    return view;
  };

  Booker.prototype.backToCalendar = function () {
    if (this.transitionLock) return;
    this.state = 'CALENDAR';
    this.root.setAttribute('data-state', 'calendar');
    this.swapView(this.renderCalendar(), 'back');
  };

  Booker.prototype.backToTime = function () {
    if (this.transitionLock) return;
    this.state = 'TIME';
    this.root.setAttribute('data-state', 'time');
    this.swapView(this.renderTime(this.selectedDay), 'back');
  };

  // ── slot pick → FORM (with past-slot guard) ─────────────────────
  Booker.prototype.pickSlot = function (slot) {
    var self = this;
    if (self.transitionLock) return;
    var slotStart = new Date(slot.start_time).getTime();
    if (slotStart <= Date.now()) {
      self.emitToast('That time has passed. Pick another.', 'warn');
      self.fetchDay(self.selectedDay).then(function (slots) {
        if (slots === null) return;
        if (self.state === 'TIME') self.replaceView(self.renderTime(self.selectedDay));
      });
      return;
    }
    self.selectedSlot = slot;
    self.idemKey = uuidV4();
    self.lastError = null;
    self.state = 'FORM';
    self.root.setAttribute('data-state', 'form');
    self.swapView(self.renderForm(false), 'forward');
  };

  // ── shared step header (back + step indicator) ──────────────────
  Booker.prototype.renderStepHeader = function (backTarget, stepText) {
    var self = this;
    var backLabel = backTarget === 'calendar' ? 'Back to calendar' : 'Back to time picker';
    return el('div', { class: 'dd-sched__step-header' }, [
      el('button', {
        class: 'dd-sched__back',
        type: 'button',
        'data-autofocus': '',
        'aria-label': backLabel,
        onclick: function () {
          if (backTarget === 'calendar') self.backToCalendar();
          else self.backToTime();
        },
      }, [
        el('span', { class: 'dd-sched__back-icon', 'aria-hidden': 'true', text: '\u2190' }),
        el('span', { class: 'dd-sched__back-label', text: 'Back' }),
      ]),
      el('p', { class: 'dd-sched__step-indicator', text: stepText }),
    ]);
  };

  // ── FORM ─────────────────────────────────────────────────────────
  Booker.prototype.renderForm = function (submitting) {
    var self = this;
    var slot = self.selectedSlot;
    var slotDate = slot ? new Date(slot.start_time) : null;

    var nameInput, emailInput, phoneInput;

    function rerender() {
      self.replaceView(self.renderForm(false));
    }

    function submit(ev) {
      ev.preventDefault();
      if (Date.now() < self.submitCooldownUntil) {
        self.emitToast('Wait a moment before retrying.', 'warn');
        return;
      }
      var name = (nameInput.value || '').trim();
      var email = (emailInput.value || '').trim();
      var phoneRaw = (phoneInput.value || '').trim();
      var phone = normalizePhoneToE164(phoneRaw);
      if (name.length < 2) {
        self.lastError = 'Please enter your full name.';
        rerender();
        return;
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        self.lastError = 'Please enter a valid email address.';
        rerender();
        return;
      }
      if (!phone) {
        self.lastError = 'Please enter a valid mobile number for text reminders.';
        rerender();
        return;
      }
      self.submitBooking({ name: name, email: email, phone: phone });
    }

    var form = el('form', {
      class: 'dd-sched__view-inner dd-sched__view-inner--form',
      novalidate: 'novalidate',
      onsubmit: submit,
    });

    // Step header — Back disabled during submit
    var stepHeader = el('div', { class: 'dd-sched__step-header' }, [
      el('button', {
        class: 'dd-sched__back',
        type: 'button',
        disabled: submitting ? 'disabled' : null,
        'aria-label': 'Back to time picker',
        onclick: function () {
          if (submitting) return;
          self.backToTime();
        },
      }, [
        el('span', { class: 'dd-sched__back-icon', 'aria-hidden': 'true', text: '\u2190' }),
        el('span', { class: 'dd-sched__back-label', text: 'Back' }),
      ]),
      el('p', { class: 'dd-sched__step-indicator', text: 'Step 3 of 3 \u2014 Confirm' }),
    ]);
    form.appendChild(stepHeader);

    form.appendChild(el('div', { class: 'dd-sched__form-summary' }, [
      el('span', { class: 'dd-sched__form-summary-label', text: 'Your time' }),
      el('span', {
        class: 'dd-sched__form-summary-value',
        text: slotDate ? formatLongDateTime(slotDate, self.timezone) : '',
      }),
    ]));

    if (self.lastError) {
      form.appendChild(el('div', {
        class: 'dd-sched__form-error',
        role: 'alert',
        text: self.lastError,
      }));
    }

    var nameLabel = el('label', { class: 'dd-sched__field' }, [
      el('span', { class: 'dd-sched__field-label', text: 'Your name' }),
    ]);
    nameInput = el('input', {
      class: 'dd-sched__field-input',
      type: 'text',
      name: 'name',
      autocomplete: 'name',
      required: 'required',
      value: self.customerName,
      placeholder: 'Sarah Patel',
      disabled: submitting ? 'disabled' : null,
      oninput: function (e) { self.customerName = e.target.value; },
    });
    nameLabel.appendChild(nameInput);
    form.appendChild(nameLabel);

    var emailLabel = el('label', { class: 'dd-sched__field' }, [
      el('span', { class: 'dd-sched__field-label', text: 'Email' }),
    ]);
    emailInput = el('input', {
      class: 'dd-sched__field-input',
      type: 'email',
      name: 'email',
      autocomplete: 'email',
      required: 'required',
      value: self.customerEmail,
      placeholder: 'you@example.com',
      disabled: submitting ? 'disabled' : null,
      oninput: function (e) { self.customerEmail = e.target.value; },
    });
    emailLabel.appendChild(emailInput);
    form.appendChild(emailLabel);

    var phoneLabel = el('label', { class: 'dd-sched__field' }, [
      el('span', { class: 'dd-sched__field-label', text: 'Mobile phone' }),
    ]);
    phoneInput = el('input', {
      class: 'dd-sched__field-input',
      type: 'tel',
      name: 'phone',
      autocomplete: 'tel',
      inputmode: 'tel',
      required: 'required',
      value: self.customerPhone,
      placeholder: '(480) 555-1234',
      disabled: submitting ? 'disabled' : null,
      oninput: function (e) { self.customerPhone = e.target.value; },
    });
    phoneLabel.appendChild(phoneInput);
    form.appendChild(phoneLabel);

    form.appendChild(el('button', {
      class: 'dd-sched__submit',
      type: 'submit',
      disabled: submitting ? 'disabled' : null,
      'aria-busy': submitting ? 'true' : null,
    }, submitting ? 'Confirming\u2026' : 'Confirm booking'));

    form.appendChild(el('p', {
      class: 'dd-sched__form-meta',
      text: "We'll email your calendar invite and text reminders to this number.",
    }));

    return form;
  };

  // ── SUBMIT → CONFIRMED / error ───────────────────────────────────
  Booker.prototype.submitBooking = function (formData) {
    var self = this;
    if (!self.selectedSlot) return;
    if (!self.idemKey) self.idemKey = uuidV4();

    self.state = 'SUBMITTING';
    self.root.setAttribute('data-state', 'submitting');
    // Replace in place — submit state is just FORM with disabled inputs
    self.replaceView(self.renderForm(true));

    fetch(apiUrl + '/api/calendly/book', {
      method: 'POST',
      credentials: 'omit',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        'Idempotency-Key': self.idemKey,
      },
      body: JSON.stringify({
        start_time: self.selectedSlot.start_time,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        timezone: self.timezone,
      }),
    }).then(function (res) {
      return res.json().catch(function () { return null; }).then(function (body) {
        return { res: res, body: body };
      });
    }).then(function (out) {
      // 200: success
      if (out.res.ok) {
        self.confirmation = (out.body && out.body.booking) || null;
        self.state = 'CONFIRMED';
        self.root.setAttribute('data-state', 'confirmed');
        self.swapView(self.renderConfirmed(), 'forward');
        self.enterConfirmedLayout();
        try {
          (window.dataLayer = window.dataLayer || []).push({
            event: 'welcome_booking_confirmed',
            booking_uri: self.confirmation && self.confirmation.uri,
          });
        } catch (_) {}
        return;
      }
      // 409: slot taken
      if (out.res.status === 409) {
        self.emitToast('That time was just booked. Pick another.', 'warn', 5000);
        self.idemKey = null;
        self.selectedSlot = null;
        self.state = 'TIME';
        self.root.setAttribute('data-state', 'time');
        // Refetch then slide back
        self.fetchDay(self.selectedDay).finally(function () {
          self.swapView(self.renderTime(self.selectedDay), 'back');
        });
        return;
      }
      // 429: rate limit
      if (out.res.status === 429) {
        self.submitCooldownUntil = Date.now() + 10000;
        self.emitToast('Too many requests. Wait 10 seconds.', 'warn', 6000);
        self.idemKey = uuidV4();
        self.state = 'FORM';
        self.root.setAttribute('data-state', 'form');
        self.replaceView(self.renderForm(false));
        return;
      }
      // 4xx: validation
      if (out.res.status >= 400 && out.res.status < 500) {
        var msg = (out.body && out.body.error && out.body.error.message)
                  || 'Check the form fields and try again.';
        self.lastError = msg;
        self.emitToast(msg, 'error', 5000);
        self.idemKey = uuidV4();
        self.state = 'FORM';
        self.root.setAttribute('data-state', 'form');
        self.replaceView(self.renderForm(false));
        return;
      }
      // 5xx
      self.lastError = 'Something went wrong on our end. Try the same slot again.';
      self.emitToast('Booking failed. Try again.', 'error', 5000);
      self.state = 'FORM';
      self.root.setAttribute('data-state', 'form');
      self.replaceView(self.renderForm(false));
    }).catch(function (err) {
      console.error('[dd-call-booker] book failed', err);
      self.lastError = "We couldn't reach our scheduler. Check your connection and try again.";
      self.emitToast('Connection issue. Try again.', 'error', 5000);
      self.state = 'FORM';
      self.root.setAttribute('data-state', 'form');
      self.replaceView(self.renderForm(false));
    });
  };

  // ── CONFIRMED ────────────────────────────────────────────────────
  Booker.prototype.showConfirmPreview = function () {
    this.confirmation = buildConfirmPreviewBooking(this);
    this.state = 'CONFIRMED';
    this.root.setAttribute('data-state', 'confirmed');
    this.root.setAttribute('data-preview-mode', 'confirm');
    clear(this.viewport);
    var view = this.renderConfirmed();
    view.classList.add('dd-sched__view');
    this.viewport.appendChild(view);
    this.mountPreviewBanner();
    this.enterConfirmedLayout();
  };

  Booker.prototype.mountPreviewBanner = function () {
    if (!this.bannerHost) return;
    clear(this.bannerHost);
    var exitUrl = confirmPreviewExitUrl();
    this.bannerHost.appendChild(el('div', {
      class: 'dd-sched__preview-banner',
      role: 'status',
    }, [
      el('p', {
        class: 'dd-sched__preview-banner-text',
        text: 'Preview mode: sample confirmation only. No Calendly booking was created.',
      }),
      el('a', {
        class: 'dd-sched__preview-banner-exit',
        href: exitUrl,
        text: 'Exit preview',
      }),
    ]));
  };

  Booker.prototype.enterConfirmedLayout = function () {
    var section = this.root.closest('.dd-call-booker');
    if (section) section.classList.add('is-booking-confirmed');
    if (this.shell) this.shell.classList.add('dd-call-booker__glass--confirmed');
    if (window.ddCallBookerSwap && typeof window.ddCallBookerSwap.toScheduler === 'function') {
      window.ddCallBookerSwap.toScheduler();
    }
    var target = section || this.root;
    requestAnimationFrame(function () {
      try {
        target.scrollIntoView({ behavior: motionReduced ? 'auto' : 'smooth', block: 'start' });
      } catch (_) {}
    });
  };

  Booker.prototype.renderConfirmed = function () {
    var self = this;
    var c = self.confirmation || {};
    var cfg = self.confirmCfg || {};
    var startDate = c.start_time ? new Date(c.start_time) : null;
    var endDate = c.end_time ? new Date(c.end_time) : startDate;
    var attendeeName = c.name || self.customerName || 'You';
    var attendeeEmail = c.email || self.customerEmail || 'your email';
    var eventTitle = cfg.eventTitle || 'Founder call with Eric';
    var detailLine = formatConfirmedDetailLine(startDate, endDate, self.timezone);
    var calEvent = buildCalendarEvent(c, cfg);
    calEvent.uid = c.uri || null;

    function confirmedSummaryRow(iconPaths, copyNodes) {
      return el('div', { class: 'dd-sched__confirmed-summary-row' }, [
        el('span', { class: 'dd-sched__confirmed-card-icon-wrap', 'aria-hidden': 'true' }, [
          svgIcon('dd-sched__confirmed-card-icon', iconPaths),
        ]),
        el('div', { class: 'dd-sched__confirmed-card-copy' }, copyNodes),
      ]);
    }

    function calendarTextBtn(label, onClick) {
      return el('button', {
        class: 'dd-sched__confirmed-cal-btn dd-sched__confirmed-cal-btn--text',
        type: 'button',
        onclick: onClick,
      }, label);
    }

    var expectItems = Array.isArray(cfg.expect) ? cfg.expect : [];
    var expectRows = [];
    var expectIcons = [
      ['M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z'],
      ['M12 3l1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5L12 3z'],
      ['M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2', 'M9 5a2 2 0 0 0 2 2h2a2 2 0 0 0 2-2M9 5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2'],
    ];
    for (var ei = 0; ei < expectItems.length; ei++) {
      var item = expectItems[ei];
      if (!item || (!item.title && !item.body)) continue;
      expectRows.push(el('li', { class: 'dd-sched__confirmed-expect-item' }, [
        el('span', { class: 'dd-sched__confirmed-expect-icon-wrap', 'aria-hidden': 'true' }, [
          svgIcon('dd-sched__confirmed-expect-icon', expectIcons[ei] || expectIcons[0]),
        ]),
        el('div', { class: 'dd-sched__confirmed-expect-copy' }, [
          item.title ? el('p', { class: 'dd-sched__confirmed-expect-title', text: item.title }) : null,
          item.body ? el('p', { class: 'dd-sched__confirmed-expect-body', text: item.body }) : null,
        ]),
      ]));
    }

    var noshowActions = [];
    if (c.reschedule_url) {
      noshowActions.push(el('a', {
        class: 'dd-sched__confirmed-noshow-link',
        href: c.reschedule_url,
        target: '_blank',
        rel: 'noopener',
        text: 'Reschedule',
      }));
    }
    if (c.cancel_url) {
      noshowActions.push(el('a', {
        class: 'dd-sched__confirmed-noshow-link dd-sched__confirmed-noshow-link--muted',
        href: c.cancel_url,
        target: '_blank',
        rel: 'noopener',
        text: 'Cancel',
      }));
    }

    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--confirmed',
      role: 'status',
      'aria-live': 'polite',
    }, [
      el('div', { class: 'dd-sched__confirmed-page' }, [
        el('div', { class: 'dd-sched__confirmed-hero' }, [
          el('span', { class: 'dd-sched__confirmed-badge' }, [
            svgIcon('dd-sched__confirmed-badge-icon', ['M5 12.5l4.2 4.2L19 7']),
            el('span', { text: 'Booking confirmed' }),
          ]),
          el('h2', { class: 'dd-sched__confirmed-heading', text: "You're booked" }),
          el('p', {
            class: 'dd-sched__confirmed-sub',
            text: 'A confirmation has been sent to ' + attendeeEmail + '.',
          }),
        ]),

        el('div', { class: 'dd-sched__confirmed-cards' }, [
          el('div', { class: 'dd-sched__confirmed-card dd-sched__confirmed-card--summary' }, [
            confirmedSummaryRow([
              'M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
            ], [
              el('p', { class: 'dd-sched__confirmed-card-title', text: eventTitle }),
              detailLine ? el('p', { class: 'dd-sched__confirmed-card-meta', text: detailLine }) : null,
            ]),
            el('div', { class: 'dd-sched__confirmed-summary-divider', 'aria-hidden': 'true' }),
            confirmedSummaryRow([
              'M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z',
              'M22 6l-10 7L2 6',
            ], [
              el('p', { class: 'dd-sched__confirmed-card-title', text: attendeeName }),
              el('p', { class: 'dd-sched__confirmed-card-meta', text: attendeeEmail }),
            ]),
            el('div', { class: 'dd-sched__confirmed-summary-divider', 'aria-hidden': 'true' }),
            el('div', { class: 'dd-sched__confirmed-summary-calendar' }, [
              confirmedSummaryRow([
                'M8 2v4', 'M16 2v4', 'M3 10h18', 'M5 4h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2z',
                'M12 11v6', 'M9 14h6',
              ], [
                el('p', { class: 'dd-sched__confirmed-card-title', text: 'Add to your calendar' }),
              ]),
              el('div', { class: 'dd-sched__confirmed-cal-row' }, [
                calendarTextBtn('Google', function () {
                  window.open(buildGoogleCalendarUrl(calEvent), '_blank', 'noopener');
                }),
                calendarTextBtn('Outlook', function () {
                  window.open(buildOutlookCalendarUrl(calEvent), '_blank', 'noopener');
                }),
                calendarTextBtn('Apple / .ics', function () {
                  downloadIcsFile(calEvent);
                }),
              ]),
            ]),
          ]),

          el('div', { class: 'dd-sched__confirmed-card dd-sched__confirmed-card--noshow' }, [
            el('span', { class: 'dd-sched__confirmed-noshow-icon-wrap', 'aria-hidden': 'true' }, [
              svgIcon('dd-sched__confirmed-noshow-icon', [
                'M12 9v4', 'M12 17h.01', 'M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z',
              ]),
            ]),
            el('div', { class: 'dd-sched__confirmed-noshow-copy' }, [
              el('p', {
                class: 'dd-sched__confirmed-noshow-title',
                text: cfg.noshowTitle || "Please don't no-show",
              }),
              el('p', {
                class: 'dd-sched__confirmed-noshow-body',
                text: cfg.noshowBody || '',
              }),
              noshowActions.length ? el('div', { class: 'dd-sched__confirmed-noshow-actions' }, noshowActions) : null,
            ]),
          ]),

          expectRows.length ? el('div', { class: 'dd-sched__confirmed-card dd-sched__confirmed-card--expect' }, [
            el('p', {
              class: 'dd-sched__confirmed-expect-eyebrow',
              text: cfg.expectHeading || 'What to expect',
            }),
            el('p', {
              class: 'dd-sched__confirmed-expect-intro',
              text: cfg.expectIntro || 'How to prepare for your call',
            }),
            el('ul', { class: 'dd-sched__confirmed-expect-list' }, expectRows),
          ]) : null,
        ]),
      ]),
    ]);
  };

  // ── ERROR_LOAD ───────────────────────────────────────────────────
  Booker.prototype.renderErrorLoad = function () {
    var self = this;
    return el('div', {
      class: 'dd-sched__view-inner dd-sched__view-inner--error',
      role: 'alert',
    }, [
      el('p', { class: 'dd-sched__error-title', text: "We can't load times right now." }),
      el('p', {
        class: 'dd-sched__error-body',
        text: 'Trying again in a moment. The Calendly link below still works.',
      }),
      el('button', {
        class: 'dd-sched__error-retry',
        type: 'button',
        'data-autofocus': '',
        onclick: function () {
          self.state = 'LOADING_INITIAL';
          self.swapView(self.renderLoading(), 'forward');
          self.loadInitial();
        },
      }, 'Retry'),
    ]);
  };

  // ── boot ─────────────────────────────────────────────────────────
  try {
    var booker = new Booker(container);
    window._ddCallBooker = booker;
    booker.mount();
    window.ddCallBookerPreviewConfirm = function () {
      if (window._ddCallBooker && typeof window._ddCallBooker.showConfirmPreview === 'function') {
        window._ddCallBooker.showConfirmPreview();
      }
    };
  } catch (err) {
    console.error('[dd-call-booker] mount failed', err);
  }
})();
